'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type IntangibleUnit = 'per hour' | 'per batch' | 'per kg' | 'per L' | 'per unit' | 'per run';

export interface IntangibleItem {
  id: string;
  name: string;
  description?: string;
  costPerUnit: number;
  unit: IntangibleUnit;
  category: string;
}

const DEFAULT_INTANGIBLES: Omit<IntangibleItem, 'id'>[] = [
  { name: 'Labor', description: 'Cost of labor per hour', costPerUnit: 15, unit: 'per hour', category: 'Labor' },
  { name: 'Oven Electricity', description: 'Electricity cost for oven usage per hour', costPerUnit: 0.5, unit: 'per hour', category: 'Utilities' },
  { name: 'Stove Gas', description: 'Gas cost for stove usage per hour', costPerUnit: 0.3, unit: 'per hour', category: 'Utilities' },
  { name: 'General Electricity', description: 'General electricity overhead per hour', costPerUnit: 0.2, unit: 'per hour', category: 'Utilities' },
  { name: 'Equipment Wear', description: 'Equipment depreciation per batch', costPerUnit: 1.0, unit: 'per batch', category: 'Equipment' },
];

export const INTANGIBLE_CATEGORIES = ['Labor', 'Utilities', 'Equipment', 'Overhead', 'Other'];
export const INTANGIBLE_UNITS: IntangibleUnit[] = ['per hour', 'per batch', 'per kg', 'per L', 'per unit', 'per run'];

interface IntangiblesContextValue {
  intangibles: IntangibleItem[];
  loading: boolean;
  addIntangible: (item: Omit<IntangibleItem, 'id'>) => Promise<void>;
  updateIntangible: (id: string, item: Partial<Omit<IntangibleItem, 'id'>>) => Promise<void>;
  deleteIntangible: (id: string) => Promise<void>;
}

const IntangiblesContext = createContext<IntangiblesContextValue | null>(null);

function rowToItem(r: any): IntangibleItem {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? undefined,
    costPerUnit: Number(r.cost_per_unit),
    unit: r.unit as IntangibleUnit,
    category: r.category,
  };
}

export function IntangiblesProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const supabase = createClient();
  const [intangibles, setIntangibles] = useState<IntangibleItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadIntangibles = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('intangibles')
        .select('*')
        .eq('company_id', company.id)
        .order('name');

      if (error) throw error;

      if (data && data.length > 0) {
        setIntangibles(data.map(rowToItem));
      } else {
        // Seed defaults for new company
        await seedDefaults(company.id);
      }
    } catch {
      setIntangibles([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  const seedDefaults = async (companyId: string) => {
    const rows = DEFAULT_INTANGIBLES.map((item) => ({
      company_id: companyId,
      name: item.name,
      description: item.description ?? null,
      cost_per_unit: item.costPerUnit,
      unit: item.unit,
      category: item.category,
    }));
    const { data } = await supabase.from('intangibles').insert(rows).select('*');
    if (data) setIntangibles(data.map(rowToItem));
  };

  useEffect(() => {
    loadIntangibles();
  }, [loadIntangibles]);

  const addIntangible = useCallback(async (item: Omit<IntangibleItem, 'id'>) => {
    if (!company?.id) return;
    const { data, error } = await supabase
      .from('intangibles')
      .insert({
        company_id: company.id,
        name: item.name,
        description: item.description ?? null,
        cost_per_unit: item.costPerUnit,
        unit: item.unit,
        category: item.category,
      })
      .select('*')
      .single();
    if (!error && data) setIntangibles((prev) => [...prev, rowToItem(data)]);
  }, [company?.id, supabase]);

  const updateIntangible = useCallback(async (id: string, item: Partial<Omit<IntangibleItem, 'id'>>) => {
    if (!company?.id) return;
    const patch: Record<string, any> = {};
    if (item.name !== undefined) patch.name = item.name;
    if (item.description !== undefined) patch.description = item.description ?? null;
    if (item.costPerUnit !== undefined) patch.cost_per_unit = item.costPerUnit;
    if (item.unit !== undefined) patch.unit = item.unit;
    if (item.category !== undefined) patch.category = item.category;

    const { error } = await supabase
      .from('intangibles')
      .update(patch)
      .eq('id', id)
      .eq('company_id', company.id);
    if (!error) setIntangibles((prev) => prev.map((i) => (i.id === id ? { ...i, ...item } : i)));
  }, [company?.id, supabase]);

  const deleteIntangible = useCallback(async (id: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('intangibles')
      .delete()
      .eq('id', id)
      .eq('company_id', company.id);
    if (!error) setIntangibles((prev) => prev.filter((i) => i.id !== id));
  }, [company?.id, supabase]);

  return (
    <IntangiblesContext.Provider value={{ intangibles, loading, addIntangible, updateIntangible, deleteIntangible }}>
      {children}
    </IntangiblesContext.Provider>
  );
}

export function useIntangibles() {
  const ctx = useContext(IntangiblesContext);
  if (!ctx) throw new Error('useIntangibles must be used within IntangiblesProvider');
  return ctx;
}
